/**
 * Discussion Store - State management for governance discussion threads
 *
 * Story FOS-4.1.3: Real-Time Discussion Updates (WebSocket)
 * Task 7: Create discussion state store
 *
 * Uses nanostores for lightweight, React-compatible state management.
 * Manages thread subscription, real-time comments, participant presence,
 * and WebSocket connection status.
 *
 * @see AC-4.1.3.1 - Thread subscription state
 * @see AC-4.1.3.6 - Offline indicator state
 *
 * @module stores/discussionStore
 */

import { atom, computed } from 'nanostores';
import type { ProposalCategory } from '../services/governanceService';

// =============================================================================
// Constants
// =============================================================================

/** Typing indicator stale threshold in milliseconds (10 seconds) */
const TYPING_STALE_THRESHOLD_MS = 10000;

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Author type for comments (human vs AI agent)
 * Matches think-tank-core Discussion.AuthorType (Candid variant pattern)
 */
export type AuthorType = { Human: null } | { Agent: { agent_id: string } };

/**
 * Discussion stage lifecycle
 * Matches think-tank-core Discussion.DiscussionStage
 */
export type DiscussionStage = 'Brainstorm' | 'Refining' | 'Ready';

/**
 * Discussion category.
 *
 * Aliased to the canonical governance `ProposalCategory` set
 * (`VALID_CATEGORIES` in governanceService.ts) so the two can never drift.
 * BL-189 fixed the service side to the full 7-variant set after GOV-FIX-001
 * (Immutable) and BL-086 (Personnel, Tier1Constitutional); BL-220 aligns the
 * discussion UI, which had continued to narrow to a 4-variant subset and so
 * could not filter or render Personnel / Immutable / Tier1Constitutional.
 */
export type DiscussionCategory = ProposalCategory;

/**
 * Canonical display metadata for every discussion / proposal category.
 *
 * SINGLE SOURCE OF TRUTH for category dropdowns, filters, and badge colours —
 * do NOT hardcode a category subset in a component (BL-220). Because the key
 * type is `DiscussionCategory` (= `ProposalCategory`), adding a new canister
 * category is a compile error here until it is given metadata, and every
 * consumer that maps over {@link DISCUSSION_CATEGORIES} picks it up for free.
 */
export const CATEGORY_METADATA: Record<
  DiscussionCategory,
  { label: string; description: string; badgeClass: string }
> = {
  Constitutional: {
    label: 'Constitutional',
    description: 'Fundamental governance changes',
    badgeClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  },
  Personnel: {
    label: 'Personnel',
    description: 'Roles, hiring, and compensation',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  Operational: {
    label: 'Operational',
    description: 'Day-to-day operations',
    badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  },
  Treasury: {
    label: 'Treasury',
    description: 'Financial allocations',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  SoftwareDevelopment: {
    label: 'Software Development',
    description: 'Technical projects',
    badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  },
  Immutable: {
    label: 'Immutable',
    description: 'Permanent, non-amendable provisions',
    badgeClass: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  },
  Tier1Constitutional: {
    label: 'Tier 1 Constitutional',
    description: 'Highest-tier constitutional changes',
    badgeClass: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  },
};

/**
 * Ordered list of category values for rendering dropdown options / filters.
 * Derived from {@link CATEGORY_METADATA} — the one place a category is declared.
 */
export const DISCUSSION_CATEGORIES = Object.keys(CATEGORY_METADATA) as DiscussionCategory[];

/**
 * Connection status type
 * @see AC-4.1.3.6 - Offline indicator
 */
export type DiscussionConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error';

/**
 * Participant presence in a discussion thread
 * @see AC-4.1.3.3 - Participant join/leave events
 */
export interface DiscussionParticipant {
  /** User's principal ID */
  id: string;
  /** User's display name */
  displayName: string;
  /** Whether this is the proposer */
  isProposer: boolean;
  /** Whether this is a contributor */
  isContributor: boolean;
  /** Timestamp when they joined */
  joinedAt: number;
}

/**
 * Real-time comment received via WebSocket
 * @see AC-4.1.3.2 - Comments broadcast within 300ms
 */
export interface RealtimeComment {
  /** Comment ID */
  id: string;
  /** Thread ID this comment belongs to */
  threadId: string;
  /** Comment content */
  content: string;
  /** Author's principal ID */
  authorId: string;
  /** Author's display name */
  authorName: string;
  /** Author type (Human or Agent) */
  authorType: AuthorType;
  /** Timestamp when posted */
  timestamp: number;
  /** Whether the comment has been retracted */
  isRetracted: boolean;
  /** Timestamp when retracted (if applicable) */
  retractedAt?: number;
  /** Net vote score (upvotes - downvotes). BL-107 / BUG-DAO-032 */
  score?: number;
  /** Number of upvotes. BL-107 / BUG-DAO-032 */
  upvotes?: number;
  /** Number of downvotes. BL-107 / BUG-DAO-032 */
  downvotes?: number;
}

/**
 * Typing indicator for a participant
 */
export interface TypingIndicator {
  /** User's principal ID */
  userId: string;
  /** User's display name */
  displayName: string;
  /** Timestamp when typing started */
  startedAt: number;
}

// =============================================================================
// Atoms - Connection State (AC-4.1.3.5, AC-4.1.3.6)
// =============================================================================

/**
 * Current WebSocket connection status
 * @see AC-4.1.3.6 - Offline indicator
 */
export const $discussionConnectionStatus = atom<DiscussionConnectionStatus>('disconnected');

/**
 * Number of reconnection attempts
 * @see AC-4.1.3.5 - Exponential backoff
 */
export const $discussionReconnectAttempts = atom<number>(0);

/**
 * Last connection error message
 */
export const $discussionConnectionError = atom<string | null>(null);

// =============================================================================
// Atoms - Thread Subscription (AC-4.1.3.1)
// =============================================================================

/**
 * Currently subscribed thread ID
 * @see AC-4.1.3.1 - Thread subscription
 */
export const $currentThreadId = atom<string | null>(null);

/**
 * Current discussion stage
 * @see AC-4.1.3.4 - Stage transitions
 */
export const $currentStage = atom<DiscussionStage | null>(null);

/**
 * Timestamp when stage last changed
 */
export const $stageChangedAt = atom<number | null>(null);

// =============================================================================
// Atoms - Participants (AC-4.1.3.3)
// =============================================================================

/**
 * Map of participant ID to presence data
 * @see AC-4.1.3.3 - Participant join/leave events
 */
export const $discussionParticipants = atom<Map<string, DiscussionParticipant>>(new Map());

// =============================================================================
// Atoms - Comments (AC-4.1.3.2)
// =============================================================================

/**
 * Real-time comments received via WebSocket
 * @see AC-4.1.3.2 - Comments broadcast within 300ms
 */
export const $realtimeComments = atom<RealtimeComment[]>([]);

// =============================================================================
// Atoms - Typing Indicators
// =============================================================================

/**
 * Currently typing participants
 */
export const $typingParticipants = atom<Map<string, TypingIndicator>>(new Map());

// =============================================================================
// Computed Atoms
// =============================================================================

/**
 * Whether currently connected to WebSocket
 * @see AC-4.1.3.6
 */
export const $isDiscussionConnected = computed(
  $discussionConnectionStatus,
  (status) => status === 'connected'
);

/**
 * Whether currently reconnecting
 * @see AC-4.1.3.5
 */
export const $isDiscussionReconnecting = computed(
  $discussionConnectionStatus,
  (status) => status === 'connecting'
);

/**
 * Whether offline (disconnected or error)
 * @see AC-4.1.3.6
 */
export const $isDiscussionOffline = computed(
  $discussionConnectionStatus,
  (status) => status === 'disconnected' || status === 'error'
);

/**
 * List of participants sorted by join time
 */
export const $participantsList = computed(
  $discussionParticipants,
  (participantsMap) => {
    return Array.from(participantsMap.values()).sort((a, b) => a.joinedAt - b.joinedAt);
  }
);

/**
 * Number of participants in the thread
 */
export const $participantCount = computed(
  $discussionParticipants,
  (participantsMap) => participantsMap.size
);

/**
 * Comments sorted by timestamp (oldest first)
 */
export const $sortedComments = computed(
  $realtimeComments,
  (comments) => [...comments].sort((a, b) => a.timestamp - b.timestamp)
);

/**
 * List of currently typing participant names
 */
export const $typingParticipantNames = computed(
  $typingParticipants,
  (typingMap) => {
    return Array.from(typingMap.values()).map((t) => t.displayName);
  }
);

// =============================================================================
// Actions - Connection Status
// =============================================================================

/**
 * Set the connection status
 * @see AC-4.1.3.6 - Offline indicator
 *
 * @param status - New connection status
 * @param error - Optional error message
 */
export function setDiscussionConnectionStatus(
  status: DiscussionConnectionStatus,
  error?: string
): void {
  $discussionConnectionStatus.set(status);

  if (error) {
    $discussionConnectionError.set(error);
  } else if (status === 'connected') {
    // Clear error and reset attempts on successful connection
    $discussionConnectionError.set(null);
    $discussionReconnectAttempts.set(0);
  }
}

/**
 * Increment reconnection attempts
 * @see AC-4.1.3.5 - Exponential backoff
 */
export function incrementDiscussionReconnectAttempts(): void {
  $discussionReconnectAttempts.set($discussionReconnectAttempts.get() + 1);
}

// =============================================================================
// Actions - Thread Subscription
// =============================================================================

/**
 * Set the current thread subscription
 * @see AC-4.1.3.1 - Thread subscription
 *
 * @param threadId - Thread ID to subscribe to
 * @param stage - Initial stage of the discussion
 */
export function setCurrentThread(threadId: string, stage: DiscussionStage): void {
  // Clear previous thread state
  $discussionParticipants.set(new Map());
  $realtimeComments.set([]);
  $typingParticipants.set(new Map());

  // Set new thread
  $currentThreadId.set(threadId);
  $currentStage.set(stage);
  $stageChangedAt.set(Date.now());
}

/**
 * Clear current thread subscription
 */
export function clearCurrentThread(): void {
  $currentThreadId.set(null);
  $currentStage.set(null);
  $stageChangedAt.set(null);
  $discussionParticipants.set(new Map());
  $realtimeComments.set([]);
  $typingParticipants.set(new Map());
}

/**
 * Update discussion stage
 * @see AC-4.1.3.4 - Stage transitions
 *
 * @param stage - New stage
 * @param changedAt - Timestamp of change
 */
export function updateDiscussionStage(stage: DiscussionStage, changedAt: number): void {
  $currentStage.set(stage);
  $stageChangedAt.set(changedAt);
}

// =============================================================================
// Actions - Participants
// =============================================================================

/**
 * Add a participant to the thread
 * @see AC-4.1.3.3 - Participant join events
 *
 * @param participant - Participant to add
 */
export function addParticipant(participant: DiscussionParticipant): void {
  const participants = $discussionParticipants.get();
  const newParticipants = new Map(participants);
  newParticipants.set(participant.id, participant);
  $discussionParticipants.set(newParticipants);
}

/**
 * Remove a participant from the thread
 * @see AC-4.1.3.3 - Participant leave events
 *
 * @param participantId - ID of participant to remove
 */
export function removeParticipant(participantId: string): void {
  const participants = $discussionParticipants.get();
  const newParticipants = new Map(participants);
  newParticipants.delete(participantId);
  $discussionParticipants.set(newParticipants);

  // Also remove from typing indicators
  const typing = $typingParticipants.get();
  const newTyping = new Map(typing);
  newTyping.delete(participantId);
  $typingParticipants.set(newTyping);
}

/**
 * Set all participants (bulk update on thread state)
 *
 * @param participants - Array of participants
 */
export function setParticipants(participants: DiscussionParticipant[]): void {
  const participantsMap = new Map<string, DiscussionParticipant>();
  for (const p of participants) {
    participantsMap.set(p.id, p);
  }
  $discussionParticipants.set(participantsMap);
}

// =============================================================================
// Actions - Comments
// =============================================================================

/**
 * Add a real-time comment
 * @see AC-4.1.3.2 - Comments broadcast within 300ms
 *
 * @param comment - Comment to add
 */
export function addRealtimeComment(comment: RealtimeComment): void {
  const comments = $realtimeComments.get();

  // Avoid duplicates
  if (comments.some((c) => c.id === comment.id)) {
    return;
  }

  $realtimeComments.set([...comments, comment]);
}

/**
 * Mark a comment as retracted
 *
 * @param commentId - ID of comment to retract
 * @param retractedAt - Timestamp of retraction
 */
export function retractComment(commentId: string, retractedAt: number): void {
  const comments = $realtimeComments.get();
  const index = comments.findIndex((c) => c.id === commentId);

  if (index !== -1) {
    const updated = [...comments];
    updated[index] = {
      ...updated[index],
      isRetracted: true,
      retractedAt,
    };
    $realtimeComments.set(updated);
  }
}

/**
 * Merge fetched comments with real-time comments
 * Used when initial data is fetched from canister
 *
 * @param fetchedComments - Comments fetched from canister
 */
export function mergeComments(fetchedComments: RealtimeComment[]): void {
  const realtime = $realtimeComments.get();
  const realtimeIds = new Set(realtime.map((c) => c.id));

  // Only add fetched comments that aren't already in realtime
  const newComments = fetchedComments.filter((c) => !realtimeIds.has(c.id));

  if (newComments.length > 0) {
    $realtimeComments.set([...newComments, ...realtime]);
  }
}

// =============================================================================
// Actions - Typing Indicators
// =============================================================================

/**
 * Set typing indicator for a participant
 *
 * @param userId - User's principal ID
 * @param displayName - User's display name
 * @param isTyping - Whether they are typing
 */
export function setTypingIndicator(userId: string, displayName: string, isTyping: boolean): void {
  const typing = $typingParticipants.get();
  const newTyping = new Map(typing);

  if (isTyping) {
    newTyping.set(userId, {
      userId,
      displayName,
      startedAt: Date.now(),
    });
  } else {
    newTyping.delete(userId);
  }

  $typingParticipants.set(newTyping);
}

/**
 * Clear stale typing indicators (older than 10 seconds)
 */
export function clearStaleTypingIndicators(): void {
  const typing = $typingParticipants.get();
  const now = Date.now();

  const newTyping = new Map(typing);
  let changed = false;

  for (const [userId, indicator] of typing) {
    if (now - indicator.startedAt > TYPING_STALE_THRESHOLD_MS) {
      newTyping.delete(userId);
      changed = true;
    }
  }

  if (changed) {
    $typingParticipants.set(newTyping);
  }
}

// =============================================================================
// Reset
// =============================================================================

/**
 * Reset all discussion store state
 */
export function resetDiscussionStore(): void {
  $discussionConnectionStatus.set('disconnected');
  $discussionReconnectAttempts.set(0);
  $discussionConnectionError.set(null);
  $currentThreadId.set(null);
  $currentStage.set(null);
  $stageChangedAt.set(null);
  $discussionParticipants.set(new Map());
  $realtimeComments.set([]);
  $typingParticipants.set(new Map());
}
